import * as React from "react";

import { ITheme } from "@fluentui/react";
import { Slider } from "@fluentui/react/lib/Slider";

import { dataUniqueId } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { AppSliderProps } from "./AppSlider.Props";
import { getClassNames, getSliderStyles } from "./AppSlider.styles";

export class AppSlider extends React.PureComponent<AppSliderProps> {
  private wrapperRef = React.createRef<HTMLDivElement>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public componentDidMount() {
    this.initializeTooltipHost();
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  public componentDidUpdate(prevProps: AppSliderProps) {
    if (
      this.props.customTooltip &&
      prevProps.customTooltip !== this.props.customTooltip
    ) {
      if (this.tooltipHost) {
        this.tooltipHost.updateTooltip(this.props.customTooltip);
      } else {
        this.initializeTooltipHost();
      }
    }
  }

  private initializeTooltipHost() {
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.props.customTooltip
    ) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.wrapperRef,
        controlId: this.props.id,
        controlLabel: this.props.customTooltip,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(
          this.context,
          ribbonExperiments.delayRenderTooltipsIsEnabled
        ),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public render() {
    const { label, disabled, min, max, value, step, text, styles, snapToStep } =
      this.props;
    const classNames = getClassNames(disabled, styles);
    return (
      <div
        className={classNames.root}
        ref={this.wrapperRef}
        data-automation-type={this.props.dataAutomationType || "AppSlider"}
        data-unique-id={dataUniqueId(this.props)}
      >
        <AppThemeConsumer>
          {(theme: ITheme) => {
            return (
              <>
                <Slider
                  onChange={this.onExecute}
                  onChanged={this.onChanged}
                  disabled={disabled}
                  showValue={false}
                  min={min}
                  max={max}
                  value={Math.floor(value)}
                  step={step}
                  styles={getSliderStyles(theme, this.props.styles)}
                  // there is a bug in Slider componet of fabric
                  // it won't render the right aria-textvalue, we use aria-label instead
                  // could be refer to https://github.com/OfficeDev/office-ui-fabric-react/issues/3256
                  aria-label={text ? `${label} ${text}` : label}
                  snapToStep={snapToStep}
                />
                {text ? (
                  <span unselectable="on" className={classNames.text}>
                    {text}
                  </span>
                ) : null}
              </>
            );
          }}
        </AppThemeConsumer>
      </div>
    );
  }

  private onExecute = (value: number) => {
    if (this.props.onExecute) {
      const { id, location } = this.props;
      this.props.onExecute({ id, value, location });
    }
  };

  private onChanged = (_: any, value: number) => {
    if (this.props.onChanged) {
      const { id, location } = this.props;
      this.props.onChanged({ id, value, location });
    }
  };
}
